import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AUTHENTICATED_FAILURE, LOGOUT_SUCCESS } from '@app/core/user/store/actions';
import { Actions, ofType } from '@ngrx/effects';
import { GetPaidForms, FormsStatus } from './models/forms.enum';
import { GetPaidService } from './services/get-paid.service';
import { takeUntil, take } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TransferMethodDetails, TransferMethodType } from './models/transfer-method';
import { MatDialog } from '@angular/material/dialog';
import { DeactivateOldPaymentComponent } from './components/deactivate-old-payment/deactivate-old-payment.component';

type StepStatus = 'warning' | 'error' | 'pending' | 'valid';
type StepStatusIcon = 'issue-full' | 'error-full' | 'check-full';

@Component({
  selector: 'vc-seller-payment-page',
  templateUrl: './seller-payment-page.component.html',
  styleUrls: ['./seller-payment-page.component.scss']
})
export class SellerPaymentPageComponent implements OnInit, OnDestroy {
  getPaidForms = GetPaidForms;
  formStatus = FormsStatus;
  transferMethodType = TransferMethodType;

  selectedForm: GetPaidForms = undefined;
  isWebView = false;
  customerInfo = undefined;
  transferMethod: TransferMethodDetails = undefined;
  ngUnsubscribe = new Subject<void>();
  userIsB2C: boolean;
  userEmail: string;
  isDesktop = false;
  lang = 'en';

  // stepItem CSS class for a step status
  stepStatus: {
    [key in FormsStatus]: StepStatus;
  } = {
    // KYC
    NOT_REQUIRED: null,
    REQUIRED: 'warning',
    FAILED: 'error',
    UNDER_REVIEW: 'pending',
    VERIFIED: 'valid',
    // transfert Method
    NOT_CREATED: 'warning',
    AWAITING_CONFIRMATION: 'warning',
    CONFIRMED: 'valid'
  };

  // Icon name for a step status
  stepIcons: {
    [key in StepStatus]: StepStatusIcon;
  } = {
    warning: 'issue-full',
    error: 'error-full',
    valid: 'check-full',
    pending: null
  };

  get transferMethodState() {
    return this.customerInfo && this.customerInfo.transferMethodState;
  }

  get verificationState() {
    return this.customerInfo && this.customerInfo.verificationState;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private getPaidService: GetPaidService,
    private action: Actions,
    private router: Router,
    private dialogService: MatDialog
  ) {
    // Redirect to homepage on logout
    this.action.pipe(takeUntil(this.ngUnsubscribe), ofType(AUTHENTICATED_FAILURE, LOGOUT_SUCCESS)).subscribe({
      next: _ => this.router.navigateByUrl('/')
    });

    this.activatedRoute.queryParams.subscribe({
      next: params => (this.isWebView = params.webView || params.webview || params.native)
    });

    this.getPaidService
      .isDesktopScreen()
      .pipe(take(1))
      .subscribe({
        next: res => (this.isDesktop = res)
      });

    // update customer Info from service
    this.getPaidService.stepState$.subscribe(
      res => {
        this.customerInfo = res;
        this.getPaidService
          .getNeedAction()
          .subscribe({ next: needAction => this.getPaidService.updateBadge(needAction) });
      },
      _ => {}
    );

    // set customer Info from resolver
    this.activatedRoute.data.subscribe(
      ({ customerInformation, transferMethod }) => {
        this.customerInfo = customerInformation;
        this.transferMethod = transferMethod;
      },
      _ => {}
    );

    // Update header information
    this.getPaidService.setPageParam();
  }

  ngOnInit() {
    this.getPaidService
      .userIsB2C()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: res => {
          if (res !== undefined) {
            if (this.customerInfo) {
              // check if already known as sole trader
              res = res && this.customerInfo.profileType !== 'INDIVIDUAL';
            }
            this.userIsB2C = res;

            // Desktop : userIsB2C is mandatory before opening forms
            if (this.isDesktop) {
              this.openDefaultForm();
            }
          }
        }
      });

    this.getPaidService
      .getUserEmail()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({ next: res => (this.userEmail = res) });

    this.lang = this.getPaidService.getUserLang();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  selectForm(id: GetPaidForms) {
    this.selectedForm = id;
  }

  backToSteps() {
    this.selectedForm = undefined;
  }

  onFormSubmitted(success: boolean) {
    if (success && this.selectedForm === GetPaidForms.TRANSFERT_METHOD) {
      this.getTransferMethod();
    }

    this.selectedForm = undefined;

    if (window) {
      window.scrollTo(0, 0);
    }

    if (success && this.isDesktop) {
      this.openDefaultForm();
    }
  }

  openDefaultForm() {
    if (!this.customerInfo) {
      this.selectForm(GetPaidForms.PERSONAL_INFORMATION);
      return;
    }

    if (
      this.customerInfo &&
      this.customerInfo.transferMethodState &&
      [FormsStatus.NOT_CREATED, FormsStatus.AWAITING_CONFIRMATION].includes(this.customerInfo.transferMethodState)
    ) {
      this.selectForm(GetPaidForms.TRANSFERT_METHOD);
      return;
    }

    if (
      this.customerInfo &&
      this.customerInfo.verificationState &&
      ['REQUIRED', 'FAILED'].includes(this.customerInfo.verificationState)
    ) {
      this.selectForm(GetPaidForms.VERIFICATION);
      return;
    }
  }

  openTransferMethodForm() {
    const isPaypal = this.transferMethod?.transferMethodType === TransferMethodType.PAYPAL_ACCOUNT;
    const shouldDeactivePopinStatus =
      this.customerInfo?.transferMethodState === FormsStatus.CONFIRMED ||
      this.customerInfo?.transferMethodState === FormsStatus.FAILED;

    if (isPaypal && shouldDeactivePopinStatus) {
      this.dialogService
        .open(DeactivateOldPaymentComponent, {
          height: 'auto',
          width: '88vw',
          maxWidth: '608px'
        })
        .afterClosed()
        .subscribe({
          next: res => {
            if (res) {
              this.selectForm(this.getPaidForms.TRANSFERT_METHOD);
            }
          }
        });
    } else {
      this.selectForm(this.getPaidForms.TRANSFERT_METHOD);
    }
  }

  getTransferMethod() {
    this.getPaidService.getTransferMethodDetails().subscribe({
      next: res => (this.transferMethod = res)
    });
  }

  get showTransferMethodDetails(): boolean {
    return this.transferMethodState === FormsStatus.CONFIRMED || this.transferMethodState === FormsStatus.FAILED;
  }

  onIsSoleTrader(event: boolean) {
    this.userIsB2C = !event;
    this.openDefaultForm();
  }
}
